import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { ButtonColor, ButtonVariant } from './Button';
import { COLOR_CLASS_MAP } from './Button.constants';
import styles from './Button.module.scss';

export interface ButtonSpanProps {
  variant?: ButtonVariant;
  color?: ButtonColor;
  underline?: boolean;
  uppercase?: boolean;
  fullWidth?: boolean;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  className?: string;
  'data-testid'?: string;
  children: ReactNode;
}

/** Non-interactive span with Button visuals. Use when the real control is a parent (e.g. `<a>` wrapping the card). */
export const ButtonSpan = ({
  variant = 'primary',
  color,
  underline: underlineProp,
  uppercase: uppercaseProp,
  fullWidth = false,
  iconBefore,
  iconAfter,
  className,
  children,
  ...props
}: ButtonSpanProps) => {
  const isUppercase = uppercaseProp ?? variant !== 'link';
  const colorClass = color && COLOR_CLASS_MAP[color];

  const spanClassName = clsx(
    styles.button,
    styles[variant],
    styles.md,
    colorClass && styles[colorClass],
    underlineProp && styles.underline,
    !isUppercase && styles.noUppercase,
    fullWidth && styles.fullWidth,
    className
  );

  return (
    <span className={spanClassName} aria-hidden="true" {...props}>
      {iconBefore && (
        <span className={styles.iconBefore} aria-hidden="true">
          {iconBefore}
        </span>
      )}
      <span
        className={styles.label}
        data-label={typeof children === 'string' ? children : undefined}
      >
        <span className={styles.labelText}>{children}</span>
      </span>
      {iconAfter && (
        <span className={styles.iconAfter} aria-hidden="true">
          {iconAfter}
        </span>
      )}
    </span>
  );
};
